import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { Room, Timeslot, Session, ScheduleItem } from '../types';
import {
  findCurrentTimeslot,
  getTimeslotProgress,
  isSessionPast,
  isTimeslotCurrent,
  isTimeslotPast,
} from '../lib/boardTimeUtils';
import { SessionQuickBookmark, SessionStatusTags } from './BoardSessionCardExtras';

interface BoardGridProps {
  rooms: Room[];
  timeslots: Timeslot[];
  sessions: Session[];
  scheduleItems: ScheduleItem[];
  /** Set of session IDs the current user has voted / shown interest for. */
  votedSessionIds?: Set<string>;
  /** Set of session IDs the user bookmarked ("want to go"). */
  bookmarkedSessionIds?: Set<string>;
  /** When true, hide sessions not in filterSessionIds. */
  interestFilter?: boolean;
  filterSessionIds?: Set<string>;
  canBookmark?: boolean;
  onToggleBookmark?: (sessionId: string) => void;
  onCellClick?: (roomId: string, timeslotIndex: number) => void;
  onSessionClick?: (sessionId: string) => void;
  /** When set, session cards are draggable and cells accept drop to move sessions */
  onSessionMove?: (sessionId: string, roomId: string, timeslotIndex: number, spanSlots: number) => void;
  /** Scroll vertically to the current or next upcoming timeslot on mount */
  scrollToCurrentTime?: boolean;
  /** Extra class on the scroll container (e.g. kiosk fullscreen) */
  scrollContainerClassName?: string;
  /** Kiosk display mode: larger type, hide votes, time indicators, expired styling */
  variant?: 'default' | 'kiosk';
}

export function BoardGrid({
  rooms,
  timeslots,
  sessions,
  scheduleItems,
  votedSessionIds,
  bookmarkedSessionIds,
  interestFilter,
  filterSessionIds,
  canBookmark,
  onToggleBookmark,
  onCellClick,
  onSessionClick,
  onSessionMove,
  scrollToCurrentTime,
  scrollContainerClassName,
  variant = 'default',
}: BoardGridProps) {
  const isKiosk = variant === 'kiosk';
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const hasScrolledToNowRef = useRef(false);
  const [now, setNow] = useState(() => Date.now());
  const [nowLineTop, setNowLineTop] = useState<number | null>(null);

  useEffect(() => {
    if (!isKiosk) return;
    const id = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, [isKiosk]);

  const updateNowLine = useCallback(() => {
    if (!isKiosk) return;

    const container = scrollContainerRef.current;
    if (!container) return;

    const currentSlot = findCurrentTimeslot(timeslots, now);
    if (!currentSlot) {
      setNowLineTop(null);
      return;
    }

    const rowEl = container.querySelector(
      `[data-timeslot-index="${currentSlot.indexInDay}"]`
    ) as HTMLElement | null;
    if (!rowEl) {
      setNowLineTop(null);
      return;
    }

    const progress = getTimeslotProgress(currentSlot, now);
    const containerRect = container.getBoundingClientRect();
    const rowRect = rowEl.getBoundingClientRect();
    const top = rowRect.top - containerRect.top + container.scrollTop + progress * rowRect.height;
    setNowLineTop(top);
  }, [isKiosk, timeslots, now]);

  useEffect(() => {
    if (!isKiosk) return;
    updateNowLine();
    const container = scrollContainerRef.current;
    container?.addEventListener('scroll', updateNowLine);
    window.addEventListener('resize', updateNowLine);
    return () => {
      container?.removeEventListener('scroll', updateNowLine);
      window.removeEventListener('resize', updateNowLine);
    };
  }, [isKiosk, updateNowLine]);

  useEffect(() => {
    if (!scrollToCurrentTime || hasScrolledToNowRef.current || timeslots.length === 0) {
      return;
    }

    const now = Date.now();
    let targetIdx = timeslots.findIndex((ts) => new Date(ts.endTs).getTime() > now);
    if (targetIdx < 0) {
      targetIdx = timeslots.length - 1;
    }

    const container = scrollContainerRef.current;
    if (!container) return;

    const targetSlot = timeslots[targetIdx];
    const rowEl = container.querySelector(
      `[data-timeslot-index="${targetSlot.indexInDay}"]`
    );
    if (rowEl) {
      rowEl.scrollIntoView({ block: 'start', behavior: 'auto' });
      hasScrolledToNowRef.current = true;
      if (isKiosk) {
        requestAnimationFrame(() => updateNowLine());
      }
    }
  }, [scrollToCurrentTime, timeslots, isKiosk, updateNowLine]);

  // Create a map: "roomId-timeslotIndex" → { item, session }
  const scheduleStartMap = new Map<string, { item: ScheduleItem; session: Session }>();

  const isSessionVisible = (sessionId: string) => {
    if (!interestFilter || !filterSessionIds) return true;
    return filterSessionIds.has(sessionId);
  };

  scheduleItems.forEach((item) => {
    const session = sessions.find((s) => s.id === item.sessionId);
    if (session && isSessionVisible(session.id)) {
      const key = `${item.roomId}-${item.timeslotIndexStart}`;
      scheduleStartMap.set(key, { item, session });
    }
  });

  // Track which cells are covered by spanning sessions (not the start cell)
  const spannedCells = new Set<string>();
  scheduleItems.forEach((item) => {
    if (!isSessionVisible(item.sessionId)) return;
    for (let i = 1; i < item.spanSlots; i++) {
      spannedCells.add(`${item.roomId}-${item.timeslotIndexStart + i}`);
    }
  });

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const totalCols = 1 + rooms.length;

  const roomColumnTemplate =
    rooms.length === 1
      ? '1fr'
      : `repeat(${rooms.length}, minmax(200px, 1fr))`;

  const rowMinHeight = isKiosk ? 88 : 72;
  const timeColumnWidth = isKiosk ? 96 : 80;

  return (
    <div
      ref={scrollContainerRef}
      className={['board-scroll-container', scrollContainerClassName].filter(Boolean).join(' ')}
      role="grid"
      aria-label="Session board"
    >
      {isKiosk && nowLineTop !== null && (
        <div
          className="board-now-line"
          style={{ top: `${nowLineTop}px` }}
          aria-hidden
        />
      )}

      <div
        className={['board-grid', isKiosk ? 'board-grid--kiosk' : ''].filter(Boolean).join(' ')}
        style={{
          gridTemplateColumns: `${timeColumnWidth}px ${roomColumnTemplate}`,
          gridTemplateRows: `auto repeat(${timeslots.length}, minmax(${rowMinHeight}px, auto))`,
          width: '100%',
          minWidth: rooms.length > 1 ? `${timeColumnWidth + rooms.length * 200}px` : undefined,
        }}
      >
        {/* Corner cell – sticky top+left */}
        <div
          className="board-corner-cell"
          role="columnheader"
          style={{ gridRow: 1, gridColumn: 1 }}
        >
          Time
        </div>

        {/* Header row (room names) – sticky top */}
        {rooms.map((room, roomIdx) => (
          <div
            key={room.id}
            className="board-header-cell"
            role="columnheader"
            style={{ gridRow: 1, gridColumn: roomIdx + 2 }}
          >
            <div className="font-semibold">{room.name}</div>
          </div>
        ))}

        {/* Time slot rows */}
        {timeslots.map((timeslot, tsIdx) => {
          const gridRowNum = tsIdx + 2;
          const slotPast = isKiosk && isTimeslotPast(timeslot, now);
          const slotCurrent = isKiosk && isTimeslotCurrent(timeslot, now);

          return (
            <React.Fragment key={timeslot.id}>
              {/* Time cell – sticky left */}
              <div
                className={[
                  'board-time-cell',
                  timeslot.isBreak ? 'bg-yellow-50' : '',
                  slotPast ? 'board-time-cell--past' : '',
                  slotCurrent ? 'board-time-cell--now' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                role="rowheader"
                data-timeslot-index={timeslot.indexInDay}
                style={{ gridRow: gridRowNum, gridColumn: 1 }}
              >
                <div className={isKiosk ? 'board-time-label' : 'text-xs'}>
                  {formatTime(timeslot.startTs)}
                </div>
                <div
                  className={
                    isKiosk
                      ? 'board-time-label board-time-label--end'
                      : 'text-xs text-gray-400'
                  }
                >
                  {formatTime(timeslot.endTs)}
                </div>
                {timeslot.label && (
                  <div
                    className={
                      isKiosk
                        ? 'board-time-label board-time-label--meta mt-1'
                        : 'text-xs text-gray-500 mt-1'
                    }
                  >
                    {timeslot.label}
                  </div>
                )}
              </div>

              {/* Break row: single cell spanning all room columns */}
              {timeslot.isBreak ? (
                <div
                  key={`${timeslot.id}-break`}
                  className={[
                    'bg-yellow-50 p-2 text-center text-sm text-yellow-700 font-medium',
                    slotPast ? 'board-break-cell--past' : '',
                    slotCurrent ? 'board-break-cell--now' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  style={{
                    gridRow: gridRowNum,
                    gridColumn: `2 / ${totalCols + 1}`,
                    borderBottom: '1px solid rgba(0,0,0,0.04)',
                  }}
                  role="gridcell"
                >
                  {timeslot.label || 'Break'}
                </div>
              ) : (
                /* Room cells */
                rooms.map((room, roomIdx) => {
                  const cellKey = `${room.id}-${timeslot.indexInDay}`;
                  const isSpanned = spannedCells.has(cellKey);

                  if (isSpanned) {
                    return null;
                  }

                  const scheduled = scheduleStartMap.get(cellKey);
                  const colNum = roomIdx + 2;

                  const handleDragOver = onSessionMove
                    ? (e: React.DragEvent) => {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'move';
                      }
                    : undefined;
                  const handleDrop = onSessionMove
                    ? (e: React.DragEvent) => {
                        e.preventDefault();
                        try {
                          const data = JSON.parse(
                            e.dataTransfer.getData('application/json') || '{}'
                          );
                          if (data.sessionId) {
                            onSessionMove(
                              data.sessionId,
                              room.id,
                              timeslot.indexInDay,
                              typeof data.spanSlots === 'number' ? data.spanSlots : 1
                            );
                          }
                        } catch {
                          // Ignore invalid drag payload
                        }
                      }
                    : undefined;

                  const interestCount =
                    (scheduled?.session.proposalVoteCount ?? 0) +
                    (scheduled?.session.voteCount ?? 0);

                  const sessionExpired =
                    isKiosk &&
                    scheduled &&
                    isSessionPast(
                      timeslots,
                      scheduled.item.timeslotIndexStart,
                      scheduled.item.spanSlots,
                      now
                    );

                  return (
                    <div
                      key={cellKey}
                      className={[
                        'p-0.5',
                        scheduled ? '' : 'hover:bg-gray-50',
                        onSessionMove ? 'droptarget' : '',
                        slotPast ? 'board-grid-cell--past' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      style={{
                        borderRight: '1px solid rgba(0,0,0,0.06)',
                        borderBottom: '1px solid rgba(0,0,0,0.04)',
                        gridRow:
                          scheduled && scheduled.item.spanSlots > 1
                            ? `${gridRowNum} / span ${scheduled.item.spanSlots}`
                            : gridRowNum,
                        gridColumn: colNum,
                        minHeight: `${rowMinHeight}px`,
                      }}
                      role="gridcell"
                      onClick={() => {
                        if (scheduled && onSessionClick) {
                          onSessionClick(scheduled.session.id);
                        } else if (onCellClick) {
                          onCellClick(room.id, timeslot.indexInDay);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          if (scheduled && onSessionClick) {
                            onSessionClick(scheduled.session.id);
                          } else if (onCellClick) {
                            onCellClick(room.id, timeslot.indexInDay);
                          }
                        }
                      }}
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                      tabIndex={0}
                      aria-label={
                        scheduled
                          ? `${scheduled.session.title} in ${room.name}`
                          : `Empty slot in ${room.name} at ${formatTime(timeslot.startTs)}`
                      }
                    >
                      {scheduled && (
                        <div
                          className={[
                            'session-card h-full',
                            onSessionMove ? 'session-card-draggable' : '',
                            canBookmark ? 'session-card--with-bookmark' : '',
                            sessionExpired ? 'session-card--expired' : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                          draggable={!!onSessionMove}
                          onDragStart={
                            onSessionMove
                              ? (e) => {
                                  e.dataTransfer.effectAllowed = 'move';
                                  e.dataTransfer.setData(
                                    'application/json',
                                    JSON.stringify({
                                      sessionId: scheduled.session.id,
                                      spanSlots: scheduled.item.spanSlots,
                                    })
                                  );
                                }
                              : undefined
                          }
                        >
                          {canBookmark && onToggleBookmark && (
                            <SessionQuickBookmark
                              bookmarked={bookmarkedSessionIds?.has(scheduled.session.id) ?? false}
                              onToggle={() => onToggleBookmark(scheduled.session.id)}
                            />
                          )}
                          <div className="session-card-title">
                            {scheduled.session.title}
                          </div>
                          {scheduled.session.people.length > 0 && (
                            <div className="session-card-presenter">
                              {scheduled.session.people
                                .filter((p) => p.role === 'presenter')
                                .map((p) => p.nickname)
                                .join(', ')}
                            </div>
                          )}
                          {/* Format · Language · Level */}
                          {(() => {
                            const f = scheduled.session.fields;
                            const chips: string[] = [];
                            if (f?.format) chips.push(f.format);
                            if (f?.language) chips.push(f.language);
                            if (f?.level) chips.push(f.level);
                            return chips.length > 0 ? (
                              <div className="flex flex-wrap gap-1 mt-0.5">
                                {chips.map((c) => (
                                  <span key={c} className="text-[10px] font-medium text-gray-600 bg-gray-100 px-1 py-px rounded">{c}</span>
                                ))}
                              </div>
                            ) : null;
                          })()}
                          {/* Tags */}
                          {(() => {
                            const raw = scheduled.session.fields?.tags;
                            if (!raw) return null;
                            const tags = typeof raw === 'string'
                              ? raw.split(',').map((s: string) => s.trim()).filter(Boolean)
                              : Array.isArray(raw) ? raw : [];
                            return tags.length > 0 ? (
                              <div className="flex flex-wrap gap-1 mt-0.5">
                                {tags.map((tag: string) => (
                                  <span key={tag} className="text-[10px] text-primary-700 bg-primary-50 px-1 py-px rounded">{tag}</span>
                                ))}
                              </div>
                            ) : null;
                          })()}
                          {interestCount > 0 && !isKiosk && (
                            <div
                              className="session-card-votes"
                              title="Interest from proposals"
                            >
                              <svg
                                className="w-3.5 h-3.5 flex-shrink-0"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                aria-hidden
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M5 15l7-7 7 7"
                                />
                              </svg>
                              <span className="tabular-nums">{interestCount}</span>
                            </div>
                          )}
                          {scheduled.session.proposals.length > 1 && (
                            <div className="text-[10px] text-gray-400 mt-0.5">
                              {scheduled.session.proposals.length} proposals
                            </div>
                          )}
                          <SessionStatusTags
                            going={bookmarkedSessionIds?.has(scheduled.session.id)}
                            interested={votedSessionIds?.has(scheduled.session.id)}
                          />
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
